import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { CustomFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import { getEnum } from 'Apis/library';
import { FILTER_STYLES } from '../AgGridCompliance.config';

const parseDepartmentNames = (data: any[]) =>
    data
        .flatMap((item: any) => Object.keys(item))
        .filter(Boolean)
        .sort();

export type DepartmentFilterModel = {
    filterType: 'departmentMultiSelect';
    values: string[];
} | null;

export const DepartmentColumnFilter = ({
    model,
    onModelChange,
}: CustomFilterProps<any, any, DepartmentFilterModel>) => {
    const searchRef = useRef<HTMLInputElement>(null);
    const hidePopupRef = useRef<(() => void) | null>(null);

    const [allDepts, setAllDepts] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>(model?.values ?? []);
    const [showDropdown, setShowDropdown] = useState(false);
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [searchFocused, setSearchFocused] = useState(false);
    // Keep a ref in sync with `selected` so doesFilterPass (used in client-side mode)
    // can read the latest selection without the callback needing to be recreated.
    const selectedRef = useRef<string[]>(model?.values ?? []);

    useEffect(() => {
        let cancelled = false;

        async function fetchDepartments() {
            try {
                const res: any = await getEnum('departments', { limit: 500 } as any);
                if (!cancelled) {
                    const data: any[] = res?.data ?? [];
                    setAllDepts(parseDepartmentNames(data));
                }
            } catch {
                if (!cancelled) {
                    setAllDepts([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchDepartments();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const vals = model?.values ?? [];
        setSelected(vals);
        selectedRef.current = vals;
    }, [model]);

    // Server-side grids: doesFilterPass is never called — the server handles filtering.
    // Client-side grids: pass rows whose `department` field is in the selected set.
    const doesFilterPass = useCallback((params: { data?: any }) => {
        if (selectedRef.current.length === 0) {
            return true;
        }
        const dept = (params.data?.department ?? '') as string;
        return selectedRef.current.includes(dept);
    }, []);
    const afterGuiAttached = useCallback((params?: { hidePopup?: () => void }) => {
        hidePopupRef.current = params?.hidePopup ?? null;
        searchRef.current?.focus();
    }, []);
    useGridFilter({ doesFilterPass, afterGuiAttached });

    const suggestions = allDepts.filter(
        (d) => d.toLowerCase().includes(search.toLowerCase()) && !selected.includes(d),
    );

    const addDept = (dept: string) => {
        setSelected((prev) => [...prev, dept]);
        setSearch('');
        setShowDropdown(false);
        searchRef.current?.focus();
    };

    const removeDept = (dept: string) => {
        setSelected((prev) => prev.filter((d) => d !== dept));
    };

    const handleApply = () => {
        selectedRef.current = selected;
        onModelChange(
            selected.length > 0
                ? { filterType: 'departmentMultiSelect', values: selected }
                : null,
        );
        hidePopupRef.current?.();
    };

    const handleReset = () => {
        selectedRef.current = [];
        setSelected([]);
        setSearch('');
        onModelChange(null);
        hidePopupRef.current?.();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setShowDropdown(true);
    };

    const handleSearchFocus = () => {
        setShowDropdown(true);
        setSearchFocused(true);
    };

    const handleSearchBlur = () => {
        setTimeout(() => {
            setShowDropdown(false);
        }, 150);
        setSearchFocused(false);
    };

    const handleItemMouseDown = (e: React.MouseEvent, dept: string) => {
        e.preventDefault();
        addDept(dept);
    };

    return (
        <div style={FILTER_STYLES.container}>
            <div style={FILTER_STYLES.deptHeading}>Department</div>

            <div style={FILTER_STYLES.deptSearchWrap}>
                <input
                    ref={searchRef}
                    type="text"
                    placeholder={loading ? 'Loading…' : 'Search departments…'}
                    value={search}
                    onChange={handleSearchChange}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    style={{
                        ...FILTER_STYLES.deptSearchInput,
                        border: searchFocused
                            ? '1px solid rgba(124,58,237,0.8)'
                            : '1px solid var(--ag-input-border-color)',
                    }}
                    disabled={loading}
                    autoComplete="off"
                />
            </div>

            {/* Suggestions dropdown */}
            {showDropdown && search.length > 0 && (
                <div style={FILTER_STYLES.deptDropdown}>
                    {suggestions.length === 0 ? (
                        <div style={FILTER_STYLES.deptEmptyMsg}>No departments found</div>
                    ) : (
                        suggestions.map((dept) => (
                            <div
                                key={dept}
                                style={{
                                    ...FILTER_STYLES.deptItem,
                                    ...(hoveredItem === dept ? FILTER_STYLES.deptItemHover : {}),
                                }}
                                role="option"
                                aria-selected={false}
                                onMouseEnter={() => setHoveredItem(dept)}
                                onMouseLeave={() => setHoveredItem(null)}
                                onMouseDown={(e) => handleItemMouseDown(e, dept)}
                            >
                                {dept}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Selected chips */}
            {selected.length > 0 && (
                <>
                    <div style={FILTER_STYLES.deptChipsLabel}>Selected ({selected.length})</div>
                    <div style={FILTER_STYLES.deptChipsRow}>
                        {selected.map((dept) => (
                            <span key={dept} style={FILTER_STYLES.deptChip} title={dept}>
                                {dept}
                                <button
                                    type="button"
                                    style={FILTER_STYLES.deptChipRemove}
                                    onClick={() => removeDept(dept)}
                                    aria-label={`Remove ${dept}`}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                </>
            )}

            {/* Actions */}
            <div style={FILTER_STYLES.deptDivider} />
            <button
                type="button"
                style={{
                    ...FILTER_STYLES.deptApplyBtn,
                    ...(selected.length === 0 ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                disabled={selected.length === 0}
                onClick={handleApply}
            >
                Apply{selected.length > 0 ? ` (${selected.length})` : ''}
            </button>
            <button
                type="button"
                style={{
                    ...FILTER_STYLES.resetButton,
                    fontSize: 13,
                    ...(selected.length === 0 && model === null ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                disabled={selected.length === 0 && model === null}
                onClick={handleReset}
            >
                Reset
            </button>
        </div>
    );
};

export default DepartmentColumnFilter;
